import type { ProductResponse } from '@malaberg/shared';
/**
 * ProductSelect — T23.
 *
 * Mantine Select that loads active products from /api/products.
 *
 * On change:
 * 1. Sets productId in brief-form-store.
 * 2. Auto-selects the first market in the product's markets array.
 * 3. Resets all library fields (angle/avatar/problem/solution/ingredients/awareness).
 *    Does NOT reset optionalGuidance or winningStructure (free-text fields).
 *
 * After product change, all library queries that depend on productId refetch
 * automatically because they watch the store's productId via scope.
 */
import { Select } from '@mantine/core';
import { useProducts } from '../../hooks/use-products';
import { useBriefFormStore } from '../../state/brief-form-store';

export function ProductSelect() {
  const { products } = useProducts();
  const setField = useBriefFormStore((s) => s.setField);
  const productId = useBriefFormStore((s) => s.productId);

  const data = products.map((p: ProductResponse) => ({
    value: p.id,
    label: p.name,
  }));

  const handleChange = (selectedId: string | null) => {
    setField('productId', selectedId);

    // Auto-select first market of the chosen product
    const product = products.find((p) => p.id === selectedId);
    const firstMarket = product?.markets[0] ?? null;
    setField('market', firstMarket);

    // Reset all library fields — free-text fields are preserved
    setField('angleEntryId', null);
    setField('avatarEntryId', null);
    setField('problemMechEntryId', null);
    setField('solutionMechEntryId', null);
    setField('ingredientEntryIds', []);
    setField('awarenessLevelEntryId', null);
    setField('landingPageUrlEntryId', null);
  };

  return (
    <Select
      label="Product"
      placeholder="Select product"
      data={data}
      value={productId}
      onChange={handleChange}
      data-testid="field-product"
    />
  );
}
